/**
 * DiseaseEditorDrawer — entity-specific drawer для «Болезни» (AK-232).
 *
 * Тонкая обвязка над ReferenceCatalogDrawer + EditorSection. Секции по
 * wireframe (docs/superpowers/prototypes/ak226-reference-hub/index.html, C):
 *   1. Идентификация (название RU + латынь)
 *   2. Классификация (категория — select из disease_categories)
 *   3. Характеристики (тип возбудителя, оптимум T/влажности, поражаемые
 *      части, потери урожая %)
 *
 * v1 показывает ТОЛЬКО поля, существующие в схеме reference.diseases
 * (migration 002 + 047). Симптомы-enrichment и культуры-хозяева M:N —
 * вне scope этого тикета (отдельный data-тикет). EN-перевод отложен —
 * NameTranslationsField c locales={['ru']}.
 *
 * Болезни НЕ имеют колонки code — идентификатор = название + латынь.
 * Legacy text `category` (NOT NULL) держим синхронным с code выбранной
 * категории.
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  EditorSection,
  ReferenceCatalogDrawer,
} from '@/components/reference/ReferenceCatalogDrawer';
import { NameTranslationsField } from '@/components/reference/NameTranslationsField';
import { StringTagsField } from '@/components/reference/StringTagsField';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { AffectedCropsField } from '@/components/reference/AffectedCropsField';
import { PhotoBank } from '@/components/reference/PhotoBank';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { useDisease } from '@/pages/platform-admin/reference/hooks/useCatalogQueries';
import type {
  AffectedCrop,
  DiseaseDetail,
  DiseaseListItem,
  DiseasePayload,
  NameTranslations,
  ReferenceCategory,
} from '@/pages/platform-admin/reference/types';

const AFFECTED_PART_OPTIONS = [
  { value: 'leaves', label: 'Листья' },
  { value: 'stem', label: 'Стебель' },
  { value: 'roots', label: 'Корни' },
  { value: 'ear', label: 'Колос' },
  { value: 'grain', label: 'Зерно' },
  { value: 'fruit', label: 'Плоды' },
  { value: 'whole_plant', label: 'Всё растение' },
];

interface FormState {
  name_translations: NameTranslations;
  latin_name: string;
  category_id: string;
  pathogen_type: string;
  optimal_temp_c_min: string;
  optimal_temp_c_max: string;
  optimal_humidity_pct_min: string;
  optimal_humidity_pct_max: string;
  affected_parts: string[];
  typical_yield_loss_pct_min: string;
  typical_yield_loss_pct_max: string;
  also_known_as: string[];
}

const EMPTY_FORM: FormState = {
  name_translations: { ru: '' },
  latin_name: '',
  category_id: '',
  pathogen_type: '',
  optimal_temp_c_min: '',
  optimal_temp_c_max: '',
  optimal_humidity_pct_min: '',
  optimal_humidity_pct_max: '',
  affected_parts: [],
  typical_yield_loss_pct_min: '',
  typical_yield_loss_pct_max: '',
  also_known_as: [],
};

const numToStr = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

/**
 * Build form from the FULL detail row (PR #290 B1). Previously this took a
 * DiseaseListItem and hardcoded pathogen_type / temps / yield-loss to '',
 * which the full-replace PATCH then wrote back as NULL — silent data loss.
 * Now every field is hydrated from DiseaseDetail.
 */
function diseaseToForm(d: DiseaseDetail): FormState {
  return {
    name_translations: { ...d.name_translations },
    latin_name: d.latin_name ?? '',
    category_id: d.category_id ?? '',
    pathogen_type: d.pathogen_type ?? '',
    optimal_temp_c_min: numToStr(d.optimal_temp_c_min),
    optimal_temp_c_max: numToStr(d.optimal_temp_c_max),
    optimal_humidity_pct_min: numToStr(d.optimal_humidity_pct_min),
    optimal_humidity_pct_max: numToStr(d.optimal_humidity_pct_max),
    affected_parts: [...(d.affected_parts ?? [])],
    typical_yield_loss_pct_min: numToStr(d.typical_yield_loss_pct_min),
    typical_yield_loss_pct_max: numToStr(d.typical_yield_loss_pct_max),
    also_known_as: [...(d.also_known_as ?? [])],
  };
}

interface FormErrors {
  name?: string;
  category?: string;
  temp?: string;
}

function validate(f: FormState): FormErrors {
  const errs: FormErrors = {};
  const hasName = Object.values(f.name_translations).some((v) => v.trim().length > 0);
  if (!hasName) errs.name = 'Требуется название';
  if (!f.category_id) errs.category = 'Выберите категорию';
  const tmin = f.optimal_temp_c_min ? Number(f.optimal_temp_c_min) : null;
  const tmax = f.optimal_temp_c_max ? Number(f.optimal_temp_c_max) : null;
  if (tmin !== null && tmax !== null && tmin > tmax) {
    errs.temp = `Минимум (${tmin}) не может быть больше максимума (${tmax})`;
  }
  return errs;
}

const numOrNull = (v: string): number | null => (v.trim() ? Number(v) : null);
const intOrNull = (v: string): number | null =>
  v.trim() ? Math.trunc(Number(v)) : null;

function toPayload(f: FormState, categories: ReferenceCategory[]): DiseasePayload {
  const trimmed: NameTranslations = {};
  for (const [k, v] of Object.entries(f.name_translations)) {
    if (v.trim()) trimmed[k] = v.trim();
  }
  const cat = categories.find((c) => c.id === f.category_id);
  return {
    name_translations: trimmed,
    latin_name: f.latin_name.trim() || null,
    common_code: null,
    // Legacy text category (NOT NULL) — keep in sync with the selected code.
    category: cat?.code ?? 'unknown',
    category_id: f.category_id || null,
    pathogen_type: f.pathogen_type.trim() || null,
    optimal_temp_c_min: numOrNull(f.optimal_temp_c_min),
    optimal_temp_c_max: numOrNull(f.optimal_temp_c_max),
    optimal_humidity_pct_min: intOrNull(f.optimal_humidity_pct_min),
    optimal_humidity_pct_max: intOrNull(f.optimal_humidity_pct_max),
    affected_parts: f.affected_parts,
    typical_yield_loss_pct_min: intOrNull(f.typical_yield_loss_pct_min),
    typical_yield_loss_pct_max: intOrNull(f.typical_yield_loss_pct_max),
    also_known_as: f.also_known_as,
  };
}

export interface DiseaseEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  disease: DiseaseListItem | null;
  categories: ReferenceCategory[];
  onSubmit: (payload: DiseasePayload) => Promise<void>;
}

export function DiseaseEditorDrawer({
  open,
  onOpenChange,
  mode,
  disease,
  categories,
  onSubmit,
}: DiseaseEditorDrawerProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  // AK-257 — affected crops drive the PhotoBank lenses; kept in sync via the
  // AffectedCropsField callback so the photobank knows which crops to offer.
  const [affectedCrops, setAffectedCrops] = useState<AffectedCrop[]>([]);

  // PR #290 B1 — load the full detail row in edit mode so the form is built
  // from DiseaseDetail (with pathogen_type / temps / yield-loss), not the
  // detail-less DiseaseListItem.
  const editId = open && mode === 'edit' && disease ? disease.id : null;
  const detailQ = useDisease(editId);
  const detailLoading = Boolean(editId) && detailQ.isLoading;

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setForm(EMPTY_FORM);
      setTouched(false);
      return;
    }
    // edit: wait for detail, then hydrate the full form.
    if (detailQ.data) {
      setForm(diseaseToForm(detailQ.data));
      setTouched(false);
    }
  }, [open, mode, detailQ.data]);

  const errors = validate(form);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await onSubmit(toPayload(form, categories));
      onOpenChange(false);
    } catch (err) {
      // AK-574 — отказ сервера виден пользователю: drawer остаётся открытым,
      // текст 409/422 в тосте. Раньше промах уходил в unhandled rejection.
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const show = (err?: string) => (touched && err ? err : undefined);

  return (
    <ReferenceCatalogDrawer
      open={open}
      onOpenChange={onOpenChange}
      mode={mode}
      entityLabel="болезни"
      headerSlot={
        form.name_translations.ru ? (
          <span className="text-sm font-medium text-foreground">
            {form.name_translations.ru}
          </span>
        ) : undefined
      }
      saveDisabled={detailLoading || (touched && hasErrors)}
      saveLoading={saving}
      onSave={handleSave}
    >
      {detailLoading ? (
        <div className="space-y-4 p-1">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
      <EditorSection number={1} title="Идентификация">
        <Label className="mb-1.5 block text-sm">
          Латинское название
        </Label>
        <Input
          value={form.latin_name}
          onChange={(e) => set('latin_name', e.target.value)}
          placeholder="Puccinia recondita"
          className="mb-4 italic"
        />
        <Label className="mb-1.5 block text-sm">
          Название (RU) <span className="text-destructive">*</span>
        </Label>
        <NameTranslationsField
          value={form.name_translations}
          onChange={(v) => set('name_translations', v)}
          locales={['ru']}
          hint="EN-перевод отложен (отдельный тикет). Сейчас ведём только RU."
          hasError={Boolean(show(errors.name))}
        />
        <Label className="mb-1.5 mt-4 block text-sm">
          Другие названия (народные/местные)
        </Label>
        <StringTagsField
          values={form.also_known_as}
          onChange={(v) => set('also_known_as', v)}
          placeholder="напр. «бурая ржавчина» (Enter добавит)"
          removeLabel="Убрать синоним"
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Народные и местные синонимы. По ним ищут в каталоге и подбирают в
          реестре СЗР наравне с основным именем.
        </p>
      </EditorSection>

      <EditorSection
        number={2}
        title="Классификация"
        hint="из disease_categories"
      >
        <Label className="mb-1.5 block text-sm">
          Категория (тип возбудителя) <span className="text-destructive">*</span>
        </Label>
        <Select
          value={form.category_id || undefined}
          onValueChange={(v) => set('category_id', v)}
        >
          <SelectTrigger
            className={show(errors.category) ? 'border-destructive' : undefined}
          >
            <SelectValue placeholder="Выберите категорию…" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name_translations.ru ?? c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {show(errors.category) && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {errors.category}
          </p>
        )}
        <p className="mt-1.5 text-xs text-muted-foreground">
          Управляется в разделе «Категории болезней».
        </p>
      </EditorSection>

      <EditorSection number={3} title="Характеристики">
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block text-sm">Тип возбудителя</Label>
            <Input
              value={form.pathogen_type}
              onChange={(e) => set('pathogen_type', e.target.value)}
              placeholder="Гриб (Basidiomycota)"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Потери урожая, %</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={form.typical_yield_loss_pct_min}
                onChange={(e) => set('typical_yield_loss_pct_min', e.target.value)}
                placeholder="min"
              />
              <Input
                type="number"
                value={form.typical_yield_loss_pct_max}
                onChange={(e) => set('typical_yield_loss_pct_max', e.target.value)}
                placeholder="max"
              />
            </div>
          </div>
        </div>

        <Label className="mb-1.5 block text-sm">
          Оптимальные условия развития
        </Label>
        <div className="mb-1 grid grid-cols-2 gap-4">
          <div>
            <Input
              type="number"
              value={form.optimal_temp_c_min}
              onChange={(e) => set('optimal_temp_c_min', e.target.value)}
              placeholder="темп. min, °C"
              className={show(errors.temp) ? 'border-destructive' : undefined}
            />
            <p className="mt-1 text-xs text-muted-foreground">температура min, °C</p>
          </div>
          <div>
            <Input
              type="number"
              value={form.optimal_temp_c_max}
              onChange={(e) => set('optimal_temp_c_max', e.target.value)}
              placeholder="темп. max, °C"
              className={show(errors.temp) ? 'border-destructive' : undefined}
            />
            <p className="mt-1 text-xs text-muted-foreground">температура max, °C</p>
          </div>
        </div>
        {show(errors.temp) && (
          <p className="mb-2 flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {errors.temp}
          </p>
        )}
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <Input
              type="number"
              value={form.optimal_humidity_pct_min}
              onChange={(e) => set('optimal_humidity_pct_min', e.target.value)}
              placeholder="влажность min, %"
            />
            <p className="mt-1 text-xs text-muted-foreground">влажность min, %</p>
          </div>
          <div>
            <Input
              type="number"
              value={form.optimal_humidity_pct_max}
              onChange={(e) => set('optimal_humidity_pct_max', e.target.value)}
              placeholder="влажность max, %"
            />
            <p className="mt-1 text-xs text-muted-foreground">влажность max, %</p>
          </div>
        </div>

        <Label className="mb-1.5 block text-sm">Поражаемые части растения</Label>
        <MultiSelectChips
          options={AFFECTED_PART_OPTIONS}
          selected={form.affected_parts}
          onChange={(v) => set('affected_parts', v)}
          placeholder="Выберите части растения…"
        />
      </EditorSection>

      {/* AK-257 — section 4: affected crops (write-path). Disabled until the
          disease is saved (Q2 two-step: needs an id for the M:N tables). */}
      <EditorSection
        number={4}
        title="Поражаемые культуры"
        hint="кого поражает болезнь"
      >
        <AffectedCropsField
          dict="diseases"
          entityId={editId}
          onCropsChange={setAffectedCrops}
        />
      </EditorSection>

      {/* AK-257 — section 5: photobank (per-culture photos). Only for a saved
          entity with ≥1 affected crop. */}
      {editId && affectedCrops.length > 0 && (
        <EditorSection number={5} title="Фотобанк" hint="фото по культурам">
          <PhotoBank dict="diseases" entityId={editId} affectedCrops={affectedCrops} />
        </EditorSection>
      )}
        </>
      )}
    </ReferenceCatalogDrawer>
  );
}

export default DiseaseEditorDrawer;
